import type { NestedKeys } from '../../types.ts';

/**
 * `en` · `builder` namespace — the companion builder + signup wizard: persona,
 * appearance, and voice steps.
 */
export const builder = {
  title: 'Build your companion',
  addAnother: 'Add a companion',
  intro: 'Design her personality and look — describe her, or upload your own concept image.',
  mode: {
    legend: 'How would you like to build her?',
    describe: 'Describe her',
    upload: 'Upload a concept',
  },
  picker: {
    legend: 'How do you want to start?',
    free: 'Free',
    paid: 'Premium',
    prebuilt: {
      title: 'Start with a ready-made companion',
      blurb: 'Meet her now — free to chat, with photos and videos ready to share.',
      cta: 'Start free',
    },
    custom: {
      title: 'Build your own',
      blurb: 'Design her look and personality from scratch. Unlock generation with credits.',
      cta: 'Build custom',
    },
  },
  steps: {
    persona: 'Persona',
    appearance: 'Appearance',
    voice: 'Voice',
    review: 'Review',
  },
  persona: {
    nameLabel: 'Name',
    namePlaceholder: 'Give her a name',
    personalityLabel: 'Personality',
    bioLabel: 'Bio',
  },
  appearance: {
    uploadLabel: 'Upload a reference (optional)',
    uploadHint: 'Your own character concept only — no real people.',
    generateLabel: 'Or describe her look',
  },
  fields: {
    name: 'Name',
    nsfwLevel: 'Content level',
    traits: 'Traits',
    backstory: 'Backstory',
    style: 'Art style',
    body: 'Body',
    hair: 'Hair',
    eyes: 'Eyes',
    ethnicity: 'Ethnicity',
    ageRange: 'Age range',
    outfit: 'Outfit',
  },
  placeholders: {
    traits: 'playful, caring, witty',
  },
  upload: {
    pick: 'Choose your concept image',
    hint: 'Your own character concept only — no real people.',
  },
  submit: 'Create companion',
  submitting: 'Creating…',
  incomplete: 'Please complete the required fields.',
  imageRequired: 'Choose a concept image to upload.',
  voice: {
    label: 'Voice style',
  },
  actions: {
    next: 'Next',
    back: 'Back',
    finish: 'Meet {companionName}',
  },
  validation: {
    nameRequired: 'Give your companion a name.',
    ageGate: 'Companions must be adults — no minor personas.',
    realPersonBlocked: 'That name or image matches a real person and cannot be used.',
  },
} as const;

export type BuilderMessages = typeof builder;

/** Typo-safe dot-path keys for the `builder` namespace. */
export type BuilderKey = NestedKeys<BuilderMessages>;
